import type { ChangeEvent } from 'react';
import { Field } from 'formik';
import { FormattedMessage, useIntl } from 'react-intl';
import TextInput from '../TextInput';
import RadioGroupInput from '../RadioGroupInput';
import FormikControlledComboBox from '../ComboBox';
import type { UncontrolledComboBoxItem } from '../ComboBox/UncontrolledComboBox';
import {
  getControlAreaRadioOptions,
  getGridOperatorsForControlArea,
  isCustomControlArea,
  type FacilityField,
  type FacilityFieldValues,
} from '../../../domain/facility';
import formStyles from '../../../styles/forms.module.scss';
import type { FormikSetFieldValueForm } from '../shared/formikTypes';

export type { FacilityFieldValues };

interface CommonFacilityFieldsProps {
  namePrefix: string;
  facilityValues?: FacilityFieldValues;
  form: FormikSetFieldValueForm<UncontrolledComboBoxItem | null>;
}

function CommonFacilityFields({
  namePrefix,
  facilityValues,
  form,
}: CommonFacilityFieldsProps) {
  const intl = useIntl();
  const fieldName = (field: FacilityField) => `${namePrefix}.${field}`;
  const controlArea = facilityValues?.controlArea;

  return (
    <>
      <div className={formStyles.inputWithVerticalSpacing}>
        <Field
          captionMsg="offerWizard.regelzone"
          name={fieldName('controlArea')}
          options={getControlAreaRadioOptions(intl)}
          component={RadioGroupInput}
          radioButtonsWrapperClass={formStyles.controlAreaRadios}
          additionalChangeAction={(event: ChangeEvent<HTMLInputElement>) => {
            const oldValue = facilityValues?.controlArea;
            const newValue = event.target.value;
            if (
              oldValue !== newValue &&
              (oldValue === 'apg' ||
                newValue === 'apg' ||
                oldValue === 'custom')
            ) {
              form
                .setFieldValue(fieldName('gridOperator'), null)
                .catch(() => undefined);
            }
          }}
        />
        {isCustomControlArea(controlArea) && (
          <div className={formStyles.inputPairSingle}>
            <Field
              name={fieldName('customControlArea')}
              component={TextInput}
              labelMsg="offerWizard.regelzone.otherTextInput"
              required
            />
          </div>
        )}
      </div>
      <div className={formStyles.inputWithVerticalSpacing}>
        <div className={formStyles.inputPairSingle}>
          <Field
            name={fieldName('gridOperator')}
            component={FormikControlledComboBox}
            options={getGridOperatorsForControlArea(
              facilityValues?.controlArea
            )}
            labelMsg="offerWizard.gridOperator"
          />
        </div>
      </div>
      <div className={formStyles.inputWithVerticalSpacing}>
        <fieldset>
          <legend className={formStyles.caption}>
            <FormattedMessage id="offerWizard.coordinates" />
          </legend>
          <div className={formStyles.inputPair}>
            <Field
              name={fieldName('latitude')}
              component={TextInput}
              labelMsg="offerWizard.lat"
              labelPosition="left"
              unit="N"
            />
            <Field
              name={fieldName('longitude')}
              component={TextInput}
              labelMsg="offerWizard.lon"
              labelPosition="left"
              unit="E"
            />
          </div>
        </fieldset>
      </div>
    </>
  );
}

export default CommonFacilityFields;
